import React, { useState } from 'react'
import { Button, Form, Input, Select, Checkbox, Modal, Space, message } from 'antd'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import {
  createAdminQuestion,
  updateAdminQuestion,
  createAdminOption,
  updateAdminOption,
  deleteAdminOption,
} from '../../../../features/poll/api-admin'
import { HStack, VStack } from 'src/react-app/ui/Stack'

const { TextArea } = Input

const QUESTION_TYPES = [
  { value: 'checkbox', label: 'Чекбоксы' },
  { value: 'radio', label: 'Радио-кнопки' },
  { value: 'select', label: 'Выпадающий список' },
  { value: 'text', label: 'Текстовое поле' },
  { value: 'textarea', label: 'Текстовая область' },
  { value: 'number', label: 'Число' },
  { value: 'switch', label: 'Переключатель' },
]

interface QuestionModalProps {
  open: boolean
  quizId: string
  question: any
  defaultStepId?: string | null
  steps: any[]
  onCancel: () => void
  onOk: () => void
}

export default function QuestionModal({
  open,
  quizId,
  question,
  defaultStepId,
  steps,
  onCancel,
  onOk,
}: QuestionModalProps) {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [questionType, setQuestionType] = useState(question?.type || 'text')
  const [questionOptions, setQuestionOptions] = useState<string[]>(
    question?.options?.map((o: any) => o.label) || [],
  )
  const [questionOptionOthers, setQuestionOptionOthers] = useState<boolean[]>(
    question?.options?.map((o: any) => o.is_other || false) || [],
  )
  const isLocked = Boolean(question?.config?.locked)

  React.useEffect(() => {
    if (open) {
      const type = question?.type || 'text'
      setQuestionType(type)
      setQuestionOptions(question?.options?.map((o: any) => o.label) || [])
      setQuestionOptionOthers(question?.options?.map((o: any) => o.is_other || false) || [])

      form.setFieldsValue({
        type,
        title: question?.title || '',
        subtitle: question?.subtitle || '',
        is_required: question?.is_required ?? true,
        step_id: question?.step_id || defaultStepId || null,
      })
    }
  }, [open, question, defaultStepId, form])

  const isChoiceType = (type: string) => ['checkbox', 'radio', 'select'].includes(type)

  const addQuestionOption = () => {
    setQuestionOptions(prev => [...prev, ''])
    setQuestionOptionOthers(prev => [...prev, false])
  }

  const removeQuestionOption = (index: number) => {
    if (question?.options?.[index]?.config?.locked) return
    setQuestionOptions(prev => prev.filter((_, i) => i !== index))
    setQuestionOptionOthers(prev => prev.filter((_, i) => i !== index))
  }

  const updateQuestionOption = (index: number, value: string) => {
    setQuestionOptions(prev => {
      const next = [...prev]
      next[index] = value
      return next
    })
  }

  const updateQuestionOptionOther = (index: number, isOther: boolean) => {
    setQuestionOptionOthers(prev => {
      if (isOther) {
        return prev.map((_, i) => i === index)
      }
      const next = [...prev]
      next[index] = isOther
      return next
    })
  }

  const handleSave = async () => {
    try {
      setLoading(true)
      const values = await form.validateFields()

      let questionId = question?.id
      if (questionId) {
        await updateAdminQuestion(quizId, questionId, values)
      } else {
        const created = await createAdminQuestion(quizId, values)
        questionId = created?.id
      }

      // Handle options for choice types
      if (questionId && isChoiceType(values.type)) {
        const existing = question?.options || []
        const keptIds = new Set<string>()

        for (let i = 0; i < questionOptions.length; i++) {
          const label = questionOptions[i].trim()
          if (!label) continue
          const isOther = questionOptionOthers[i] || false

          if (i < existing.length) {
            await updateAdminOption(quizId, questionId, existing[i].id, {
              label,
              is_other: isOther,
            })
            keptIds.add(existing[i].id)
          } else {
            await createAdminOption(quizId, questionId, { label, is_other: isOther })
          }
        }

        for (const option of existing) {
          if (!keptIds.has(option.id) && !option.config?.locked) {
            await deleteAdminOption(quizId, questionId, option.id)
          }
        }
      }

      message.success('Вопрос сохранён')
      onOk()
    } catch (e: any) {
      message.error(e?.message || 'Не удалось сохранить вопрос')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title={question ? 'Редактировать вопрос' : 'Создать вопрос'}
      open={open}
      onOk={handleSave}
      onCancel={onCancel}
      confirmLoading={loading}
      destroyOnHidden
    >
      <Form form={form} layout="vertical">
        <Form.Item name="step_id" label="Шаг">
          <Select placeholder="Выберите шаг" allowClear>
            {steps.map((step: any) => (
              <Select.Option key={step.id} value={step.id}>
                {step.title}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="type" label="Тип вопроса" rules={[{ required: true }]}>
          <Select
            value={questionType}
            onChange={setQuestionType}
            options={QUESTION_TYPES}
            disabled={isLocked}
          />
        </Form.Item>

        <Form.Item name="title" label="Заголовок" rules={[{ required: true }]}>
          <Input placeholder="Введите текст вопроса" />
        </Form.Item>

        <Form.Item name="subtitle" label="Подзаголовок / пояснение">
          <TextArea rows={2} placeholder="Необязательное пояснение к вопросу" />
        </Form.Item>

        <Form.Item name="is_required" valuePropName="checked">
          <Checkbox>Обязательный к ответу</Checkbox>
        </Form.Item>

        {isChoiceType(questionType) && (
          <Form.Item label="Варианты ответа">
            <Space direction="vertical" style={{ width: '100%', gap: 20 }}>
              {questionOptions.map((option, index) => (
                <VStack key={index} gap="8" align="start" style={{ width: '100%' }}>
                  <HStack gap={'8'} style={{ width: '100%' }}>
                    <Input
                      value={option}
                      onChange={e => updateQuestionOption(index, e.target.value)}
                      placeholder={`Вариант ${index + 1}`}
                    />
                    <Button
                      icon={<DeleteOutlined />}
                      onClick={() => removeQuestionOption(index)}
                      danger
                      title="Удалить"
                      disabled={Boolean(question?.options?.[index]?.config?.locked)}
                    />
                  </HStack>

                  {questionType === 'radio' && (
                    <Checkbox
                      checked={questionOptionOthers[index] || false}
                      onChange={e => updateQuestionOptionOther(index, e.target.checked)}
                      disabled={questionOptionOthers.some((isOther, i) => isOther && i !== index)}
                    >
                      Добавить текстовое поле
                    </Checkbox>
                  )}
                </VStack>
              ))}
              <Button
                type="dashed"
                onClick={addQuestionOption}
                icon={<PlusOutlined />}
                style={{ width: '100%' }}
                disabled={isLocked}
              >
                Добавить вариант
              </Button>
            </Space>
          </Form.Item>
        )}
      </Form>
    </Modal>
  )
}
